// MedConfig keys (Spec List 12; build plan §7.1 #1): what each key holds, who
// may edit it (plan §5), whether go-live needs it, and the one validator every
// save goes through. Pure, so the SPA renders its per-key editors from these
// same definitions (as Account.tsx uses ../server/phone).
//
// Who edits what (plan §5):
//   config.operational (System Admin, Supervisor, RN): Windows, Cities,
//     HeldRefusedReasons, and ReceivedExceptionCodes (a vocabulary like
//     held/refused — a reading, recorded in plan §7.5).
//   config.admin (System Admin): everything else — insurance, contacts,
//     signer, dosing authority (named in §5), and the keys §5 does not name
//     (session dates, notification rules, digest, provider role, sender,
//     the roster schedule).
//
// The medical advisor appears only as a ROLE with contact routes (plan §0.5):
// Contacts.remoteProvider has no name field, and the standing order records
// the signer's role and credential, never a person's name.

import type { Capability } from "../roles.js";

export type FieldKind = "text" | "email" | "phone" | "date" | "time" | "url" | "number" | "yn" | "choice" | "lines" | "group" | "rows";

export interface Field {
  name: string;
  label: string;
  kind: FieldKind;
  /** Must be filled when the key is saved (not blank). */
  required?: boolean;
  hint?: string;
  /** "choice" only. */
  options?: readonly string[];
  /** "group" (one object) and "rows" (a list of objects). */
  fields?: readonly Field[];
  /** "lines": each line must be an email address. */
  linesAreEmails?: boolean;
}

export interface ConfigKeyDef {
  key: string;
  label: string;
  /** One sentence for the editor. */
  help: string;
  cap: Extract<Capability, "config.admin" | "config.operational">;
  /** Blank blocks go-live (the readiness banner lists it). */
  requiredForGoLive: boolean;
  /** The value's shape: a single "lines"/"rows" field holds a bare array; otherwise an object. */
  shape: "object" | "lines" | "rows";
  fields: readonly Field[];
}

const NOTE: Field = { name: "note", label: "Note", kind: "text", hint: "Optional, for staff." };
const phone = (name: string, label: string, required = false): Field => ({ name, label, kind: "phone", required });
const place = (name: string, label: string, withAddress = false): Field => ({
  name,
  label,
  kind: "group",
  fields: [
    { name: "name", label: "Name", kind: "text", required: true },
    phone("phone", "Phone", true),
    ...(withAddress ? [{ name: "address", label: "Address", kind: "text" } as Field] : []),
  ],
});

/** Every Spec §2 ConfigKey, in Spec order, then this app's own scheduling key. */
export const CONFIG_KEY_DEFS: readonly ConfigKeyDef[] = [
  {
    key: "SessionDates",
    label: "Session dates",
    help: "The session week. Check-in day is when \"18 at check-in\" is measured.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "start", label: "First day", kind: "date", required: true },
      { name: "end", label: "Last day", kind: "date", required: true },
      { name: "checkInOn", label: "Check-in day", kind: "date", required: true },
      { name: "nextSessionStartOn", label: "Next year's first day", kind: "date", hint: "For the supplies expiry rule." },
      NOTE,
    ],
  },
  {
    key: "Windows",
    label: "Medication windows",
    help: "The year's medication times, in order. Staff set these each year.",
    cap: "config.operational",
    requiredForGoLive: true,
    shape: "rows",
    fields: [
      { name: "code", label: "Code", kind: "text", required: true, hint: "Short, e.g. AM" },
      { name: "name", label: "Name", kind: "text", required: true },
      { name: "startTime", label: "Starts", kind: "time", required: true },
      { name: "endTime", label: "Ends", kind: "time", required: true },
    ],
  },
  {
    key: "Cities",
    label: "Cities",
    help: "This year's city names, one per line.",
    cap: "config.operational",
    requiredForGoLive: true,
    shape: "lines",
    fields: [],
  },
  {
    key: "OtcDosingAuthority",
    label: "OTC dosing authority",
    help: "Where OTC doses come from: a signed standing order, or the product label.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "kind", label: "Source", kind: "choice", options: ["StandingOrder", "ProductLabel"], required: true },
      { name: "signerRole", label: "Standing order signed by (role)", kind: "text", hint: "A role, never a name." },
      { name: "credential", label: "Signer's credential", kind: "text" },
      { name: "signedOn", label: "Signed on", kind: "date" },
      { name: "docKey", label: "Document key", kind: "text", hint: "The signed order's MedDocuments key, once attached." },
      NOTE,
    ],
  },
  {
    key: "HeldRefusedReasons",
    label: "Held / refused reasons",
    help: "The choices staff pick from when a dose is held or refused, one per line.",
    cap: "config.operational",
    requiredForGoLive: true,
    shape: "lines",
    fields: [],
  },
  {
    key: "ReceivedExceptionCodes",
    label: "Medication receipt exceptions",
    help: "The exceptions recorded when medication arrives, one per line.",
    cap: "config.operational",
    requiredForGoLive: true,
    shape: "lines",
    fields: [],
  },
  {
    key: "Insurance",
    label: "Insurance",
    help: "The program's accident policy and its claims administrator.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "policyNumber", label: "Policy number", kind: "text", required: true },
      { name: "policyholderName", label: "Policyholder", kind: "text", required: true },
      { name: "policyholderAddress", label: "Policyholder address", kind: "text" },
      { name: "administratorName", label: "Claims administrator", kind: "text", required: true },
      { name: "administratorEmail", label: "Administrator email", kind: "email" },
      phone("administratorFax", "Administrator fax"),
      phone("administratorPhone", "Administrator phone"),
      { name: "noticeDays", label: "Notice period (days)", kind: "number", hint: "Usually 90." },
      NOTE,
    ],
  },
  {
    key: "ClaimSigner",
    label: "Claim form signer",
    help: "Who signs Part I of the claim form for this program.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "name", label: "Name", kind: "text", required: true },
      { name: "title", label: "Title", kind: "text", required: true },
      phone("phone", "Phone", true),
      NOTE,
    ],
  },
  {
    key: "Contacts",
    label: "Contacts",
    help: "Who staff call, and where the director's mail goes.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      place("campusResponders", "Campus first responders"),
      place("localEms", "Local EMS"),
      {
        name: "remoteProvider",
        label: "Remote medical provider",
        kind: "group",
        hint: "By role only, never a name.",
        fields: [
          { name: "role", label: "Role", kind: "text", required: true },
          phone("phone", "Phone"),
          { name: "email", label: "Email", kind: "email" },
          { name: "teamsLink", label: "Teams link", kind: "url" },
        ],
      },
      place("urgentCare", "Urgent care", true),
      place("hospital", "Hospital", true),
      phone("medStationPhone", "Med station phone"),
      { name: "directorEmail", label: "Director's email", kind: "email", required: true },
      NOTE,
    ],
  },
  {
    key: "NotificationRules",
    label: "Notification rules",
    help: "When parent notifications are due, and which dispositions need a phone call.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "emailDueHours", label: "Email due within (hours)", kind: "number", required: true, hint: "Usually 24." },
      { name: "phoneTierDispositions", label: "Dispositions that need a phone call", kind: "lines" },
      NOTE,
    ],
  },
  {
    key: "DocRequestDigest",
    label: "Document request digest",
    help: "When a document request counts as overdue, and who gets the daily digest.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "olderThanDays", label: "Overdue after (days)", kind: "number", required: true, hint: "Suggested 7." },
      { name: "recipients", label: "Digest recipients", kind: "lines", linesAreEmails: true },
      NOTE,
    ],
  },
  {
    key: "ProviderRole",
    label: "Provider coverage",
    help: "Whether an on-site Supervisor is present and a remote provider is engaged.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [
      { name: "onSiteSupervisorPresent", label: "On-site Supervisor present", kind: "yn", required: true },
      { name: "remoteProviderEngaged", label: "Remote provider engaged", kind: "yn", required: true },
      NOTE,
    ],
  },
  {
    key: "MailFrom",
    label: "Sender address",
    help: "The program's no-reply address for this app's email.",
    cap: "config.admin",
    requiredForGoLive: true,
    shape: "object",
    fields: [{ name: "address", label: "Address", kind: "email", required: true }, NOTE],
  },
  {
    key: "RosterSweep",
    label: "Roster schedule",
    help: "Read the applications into the roster automatically every 15 minutes. Off unless set to Yes.",
    cap: "config.admin",
    requiredForGoLive: false,
    shape: "object",
    fields: [{ name: "scheduled", label: "Read automatically", kind: "yn", required: true }, NOTE],
  },
];

/** The keys Spec §2 lists (the 13 the test packs fill). */
export const SPEC_CONFIG_KEYS = CONFIG_KEY_DEFS.filter((d) => d.key !== "RosterSweep").map((d) => d.key);

export function configKeyDef(key: string): ConfigKeyDef | null {
  return CONFIG_KEY_DEFS.find((d) => d.key === key) ?? null;
}

// ---- validation ------------------------------------------------------------------

export class ConfigValueError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ConfigValueError";
  }
}

const MAX_TEXT = 500;
const MAX_ITEMS = 100;
const RE = {
  email: /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]+$/,
  phone: /^[0-9()+.\- ]{7,25}$/,
  date: /^\d{4}-\d{2}-\d{2}$/,
  time: /^([01]\d|2[0-3]):[0-5]\d$/,
  url: /^https:\/\/[^\s<>"]+$/,
};

function realDate(s: string): boolean {
  if (!RE.date.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

function cleanLines(v: unknown, label: string, emails: boolean): string[] {
  const raw = Array.isArray(v) ? v : typeof v === "string" ? v.split(/\r?\n/) : null;
  if (!raw) throw new ConfigValueError(`${label}: expected a list.`);
  const out = raw.map((x) => (typeof x === "string" ? x.trim() : "")).filter(Boolean);
  if (raw.some((x) => typeof x !== "string")) throw new ConfigValueError(`${label}: every line must be text.`);
  if (out.length > MAX_ITEMS) throw new ConfigValueError(`${label}: at most ${MAX_ITEMS} lines.`);
  for (const x of out) {
    if (x.length > MAX_TEXT) throw new ConfigValueError(`${label}: a line is too long.`);
    if (emails && !RE.email.test(x)) throw new ConfigValueError(`${label}: "${x}" is not an email address.`);
  }
  if (new Set(out.map((x) => x.toLowerCase())).size !== out.length) throw new ConfigValueError(`${label}: a line appears twice.`);
  return out;
}

function cleanScalar(f: Field, v: unknown, label: string): string | number | undefined {
  if (v === undefined || v === null || (typeof v === "string" && v.trim() === "")) {
    if (f.required) throw new ConfigValueError(`${label} is required.`);
    return undefined;
  }
  if (f.kind === "number") {
    const n = typeof v === "number" ? v : typeof v === "string" ? Number(v.trim()) : NaN;
    if (!Number.isInteger(n) || n < 0 || n > 10_000) throw new ConfigValueError(`${label} must be a whole number.`);
    return n;
  }
  if (typeof v !== "string") throw new ConfigValueError(`${label} must be text.`);
  const s = v.trim();
  if (s.length > MAX_TEXT) throw new ConfigValueError(`${label} is too long.`);
  switch (f.kind) {
    case "email":
      if (!RE.email.test(s)) throw new ConfigValueError(`${label} must be an email address.`);
      return s.toLowerCase();
    case "phone":
      if (!RE.phone.test(s) || s.replace(/\D/g, "").length < 7) throw new ConfigValueError(`${label} must be a phone number.`);
      return s;
    case "date":
      if (!realDate(s)) throw new ConfigValueError(`${label} must be a date (YYYY-MM-DD).`);
      return s;
    case "time":
      if (!RE.time.test(s)) throw new ConfigValueError(`${label} must be a time (HH:MM, 24-hour).`);
      return s;
    case "url":
      if (!RE.url.test(s)) throw new ConfigValueError(`${label} must be an https:// link.`);
      return s;
    case "yn":
      if (s !== "Y" && s !== "N") throw new ConfigValueError(`${label} must be Y or N.`);
      return s;
    case "choice":
      if (!f.options?.includes(s)) throw new ConfigValueError(`${label} must be one of: ${f.options?.join(", ")}.`);
      return s;
    default:
      return s;
  }
}

function cleanObject(fields: readonly Field[], v: unknown, label: string): Record<string, unknown> {
  if (!v || typeof v !== "object" || Array.isArray(v)) throw new ConfigValueError(`${label}: expected a set of fields.`);
  const src = v as Record<string, unknown>;
  const known = new Set(fields.map((f) => f.name));
  for (const k of Object.keys(src)) {
    if (!known.has(k)) throw new ConfigValueError(`${label}: unknown field "${k}".`);
  }
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const fl = `${label === "" ? "" : `${label} › `}${f.label}`;
    const val = src[f.name];
    if (f.kind === "group") {
      const empty = val === undefined || val === null || (typeof val === "object" && Object.values(val as object).every((x) => x === "" || x === null || x === undefined));
      if (empty) {
        if (f.required) throw new ConfigValueError(`${fl} is required.`);
        continue;
      }
      out[f.name] = cleanObject(f.fields ?? [], val, fl);
    } else if (f.kind === "rows") {
      out[f.name] = cleanRows(f.fields ?? [], val, fl);
    } else if (f.kind === "lines") {
      const lines = val === undefined || val === null ? [] : cleanLines(val, fl, Boolean(f.linesAreEmails));
      if (f.required && lines.length === 0) throw new ConfigValueError(`${fl} is required.`);
      if (lines.length) out[f.name] = lines;
    } else {
      const s = cleanScalar(f, val, fl);
      if (s !== undefined) out[f.name] = s;
    }
  }
  return out;
}

function cleanRows(fields: readonly Field[], v: unknown, label: string): Record<string, unknown>[] {
  if (!Array.isArray(v)) throw new ConfigValueError(`${label}: expected a list of rows.`);
  if (v.length > MAX_ITEMS) throw new ConfigValueError(`${label}: at most ${MAX_ITEMS} rows.`);
  return v.map((row, i) => cleanObject(fields, row, `${label} row ${i + 1}`));
}

/** Rules across fields, after each field is clean. */
function crossCheck(key: string, v: Record<string, unknown> | unknown[]): void {
  if (key === "SessionDates") {
    const d = v as Record<string, string>;
    if (d.end < d.start) throw new ConfigValueError("Session dates: the last day is before the first day.");
    if (d.checkInOn < d.start || d.checkInOn > d.end) throw new ConfigValueError("Session dates: check-in day must fall within the session.");
    if (d.nextSessionStartOn && d.nextSessionStartOn <= d.end) throw new ConfigValueError("Session dates: next year's first day must be after this session.");
  }
  if (key === "Windows") {
    const rows = v as Record<string, string>[];
    const codes = rows.map((r) => r.code.toUpperCase());
    if (new Set(codes).size !== codes.length) throw new ConfigValueError("Medication windows: each code must be different.");
    for (const r of rows) {
      if (r.endTime <= r.startTime) throw new ConfigValueError(`Medication windows: ${r.code} ends before it starts.`);
    }
  }
  if (key === "OtcDosingAuthority") {
    const d = v as Record<string, string>;
    if (d.kind === "StandingOrder" && (!d.signerRole || !d.credential || !d.signedOn)) {
      throw new ConfigValueError("OTC dosing authority: a standing order needs the signer's role, credential and date.");
    }
  }
}

/**
 * Validates and normalizes a value for `key`. Returns the JSON to store, or ""
 * for blank (null, undefined or an empty list clears the key). Throws
 * ConfigValueError with a message fit to show staff.
 */
export function cleanConfigValue(key: string, value: unknown): string {
  const def = configKeyDef(key);
  if (!def) throw new ConfigValueError(`Unknown setting "${key}".`);
  if (value === null || value === undefined || value === "") return "";
  let clean: Record<string, unknown> | unknown[];
  if (def.shape === "lines") {
    clean = cleanLines(value, def.label, false);
    if ((clean as string[]).length === 0) return "";
  } else if (def.shape === "rows") {
    clean = cleanRows(def.fields, value, def.label);
    if ((clean as unknown[]).length === 0) return "";
  } else {
    clean = cleanObject(def.fields, value, "");
  }
  crossCheck(key, clean);
  return JSON.stringify(clean);
}

/** The stored value parsed, or null when blank or unreadable. */
export function parseConfigValue(valueJson: string): unknown {
  if (!valueJson.trim()) return null;
  try {
    return JSON.parse(valueJson);
  } catch {
    return null;
  }
}
